import InboxOutlinedIcon from "@mui/icons-material/InboxOutlined"
import NotificationsOutlinedIcon from "@mui/icons-material/NotificationsOutlined"
import Badge from "@mui/material/Badge"
import IconButton from "@mui/material/IconButton"
import Typography from "@mui/material/Typography"
import { styled } from "@mui/material/styles"
import { useState } from "react"
import { useSelector } from "react-redux"

import Row from "../../../common/components/Row"
import TextSkeleton from "../../../common/components/TextSkeleton"
import {
  canAccessOwnerAccountSettings,
  isConcierge,
  isExecutiveCaller,
  isTeamMember,
} from "../../users/accountRole"
import {
  selectAccountOwnerName,
  selectProfile,
  selectProfileLoaded,
  selectSafeLengthFullName,
} from "../../users/reducer"
import { selectNotifications, selectNotificationsStatus } from "../reducer"
import AccountSwitcher from "./AccountSwitcher"
import NotificationsPopover from "./NotificationsPopover"
import SetupProgressChecklistButton from "./SetupProgressChecklistButton"
import { useInboxNotificationsContext } from "./inboxNotifications/InboxNotificationsContext"
import InboxNotificationsPopover from "./inboxNotifications/InboxNotificationsPopover"
import { INBOX_CHANNELS } from "./inboxNotifications/inboxNotificationTypes"

const INBOX_TAB_STORAGE_KEY = "crm-inbox-popover-tab"

function readStoredInboxTab() {
  try {
    const value = window.sessionStorage.getItem(INBOX_TAB_STORAGE_KEY)
    if (
      value === INBOX_CHANNELS.email ||
      value === INBOX_CHANNELS.sms ||
      value === INBOX_CHANNELS.voicemail
    ) {
      return value
    }
  } catch (_err) {
    return INBOX_CHANNELS.email
  }
  return INBOX_CHANNELS.email
}

function writeStoredInboxTab(channel) {
  try {
    window.sessionStorage.setItem(INBOX_TAB_STORAGE_KEY, channel)
  } catch (_err) {
    return undefined
  }
}

export default function NavbarRightSection() {
  const [notificationsPopoverAnchorElem, setNotificationsPopoverAnchorElem] =
    useState(null)
  const [inboxPopoverAnchor, setInboxPopoverAnchor] = useState(null)
  const [inboxTab, setInboxTab] = useState(readStoredInboxTab)
  const [clearingInboxChannel, setClearingInboxChannel] = useState(false)

  const profile = useSelector(selectProfile)
  const profileLoaded = useSelector(selectProfileLoaded)
  const fullName = useSelector(selectSafeLengthFullName)
  const accountOwnerName = useSelector(selectAccountOwnerName)
  const notifications = useSelector(selectNotifications)
  const notificationsStatus = useSelector(selectNotificationsStatus)
  const executiveCaller = isExecutiveCaller(profile)
  const concierge = isConcierge(profile)
  const teamMember = isTeamMember(profile)
  const showOwnerSetupChecklist = canAccessOwnerAccountSettings(profile)
  const showAccountContextBadge =
    Boolean(accountOwnerName) && (teamMember || executiveCaller || concierge)
  const accountBadgeVariant = concierge
    ? "concierge"
    : executiveCaller
    ? "executive_caller"
    : "member"

  const inbox = useInboxNotificationsContext()

  const notificationCount = notifications.length
  const inboxTitle = `Inbox: ${inbox.counts.voicemail} voicemail, ${inbox.counts.email} email, ${inbox.counts.sms} SMS`

  function handleInboxChannelChange(channel) {
    setInboxTab(channel)
    writeStoredInboxTab(channel)
  }

  async function handleClearInboxChannel(channel) {
    setClearingInboxChannel(true)
    try {
      await inbox.clearChannel(channel)
    } finally {
      setClearingInboxChannel(false)
    }
  }

  return (
    <Row sx={{ alignItems: "center", gap: { xs: 0.75, sm: 1.25 } }}>
      <WelcomeRow sx={{ alignItems: "center", gap: 0.5, minWidth: 0 }}>
        <Typography
          variant="h4"
          sx={{
            display: { xs: "none", sm: "inline" },
            whiteSpace: "nowrap",
          }}
        >
          👋 Welcome back!,{" "}
        </Typography>
        {profileLoaded ? (
          <Typography
            variant="h4"
            component="span"
            color="text.pink"
            sx={{
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
              maxWidth: { xs: 120, sm: 200, md: "none" },
            }}
          >
            {fullName}
          </Typography>
        ) : (
          <TextSkeleton width={10} height="1.25em" />
        )}
      </WelcomeRow>

      {showAccountContextBadge && (
        <AccountContextRow sx={{ alignItems: "center", gap: 0.75 }}>
          <AccountSwitcher
            ownerName={accountOwnerName}
            badgeVariant={accountBadgeVariant}
          />
        </AccountContextRow>
      )}
      {showOwnerSetupChecklist && <SetupProgressChecklistButton />}
      {concierge && (
        <IconButton
          title={inboxTitle}
          aria-label={inboxTitle}
          onClick={(event) => {
            if (
              typeof window.Notification !== "undefined" &&
              window.Notification.permission === "default"
            ) {
              window.Notification.requestPermission()
            }
            setInboxPopoverAnchor(event.currentTarget)
            inbox.refresh()
          }}
        >
          <NotificationsBadge
            badgeContent={inbox.counts.total}
            color="secondary"
          >
            <InboxOutlinedIcon />
          </NotificationsBadge>
        </IconButton>
      )}
      <IconButton
        onClick={(event) =>
          setNotificationsPopoverAnchorElem(event.currentTarget)
        }
      >
        <NotificationsBadge
          badgeContent={notificationCount}
          color="secondary"
          variant={notificationCount ? "standard" : "dot"}
        >
          <NotificationsOutlinedIcon />
        </NotificationsBadge>
      </IconButton>
      {concierge ? (
        <InboxNotificationsPopover
          anchorEl={inboxPopoverAnchor}
          onClose={() => setInboxPopoverAnchor(null)}
          activeChannel={inboxTab}
          onChannelChange={handleInboxChannelChange}
          counts={inbox.counts}
          lists={inbox.lists}
          status={inbox.status}
          onClearChannel={handleClearInboxChannel}
          clearing={clearingInboxChannel}
        />
      ) : null}
      <NotificationsPopover
        notifications={notifications}
        status={notificationsStatus}
        anchorEl={notificationsPopoverAnchorElem}
        onClose={() => setNotificationsPopoverAnchorElem(null)}
      />
    </Row>
  )
}

const WelcomeRow = styled(Row)({
  flexShrink: 1,
  minWidth: 0,
})

const AccountContextRow = styled(Row)({
  flexShrink: 0,
})

const NotificationsBadge = styled(Badge)(({ theme }) => ({
  "& .MuiBadge-badge": {
    right: "3px",
  },
}))
